import React, { useState, useEffect, useContext } from 'react';
import { useApi } from '../../hooks/useApi';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import { AuthContext } from '../../AuthContext';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import TileFileCenter from './TileFileCenter';
import Autocomplete from '@mui/material/Autocomplete';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import TileAnnouncments from './TileAnnouncements';
import TileReport from './TileReport';
import TileAssessment from './TileAssessmentCenter';
import TileAuditCenter from './TileAuditCenter';
import { LinearProgress, Paper, Card, CardContent, Fade, Grow, alpha, useTheme } from '@mui/material';
import TileSignatureCenter from './TileSignatureCenter';
import TileFinanceCenter from './TileFinanceCenter';
import TileTrainingCenter from './TileTrainingCenter';
import TileServiceCenter from './TileServiceCenter';
import TileAdminEnvelopeOpen from './TileAdminEnvelopeOpen';
import TileAdminEnvelopeSigned from './TileAdminEnvelopeSigned';
import TileProcessingCenter from './TileProcessingCenter';
import TileOperationsCenter from './TileOperationCenter';
import TileQbr from './TileQbr';
import TileCustomerCenter from './TileCustomerCenter';
import TileWarrantyCenter from './TileWarrantyCenter';
import TileFleetReport from './TileFleetReport';
import TileEndpointDevices from './TileEndpointDevices';
import TileBotReports from './TileBotReports';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import IconButton from '@mui/material/IconButton';
import ImageHello from "../../media/Lamp.png";
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AssessmentIcon from '@mui/icons-material/Assessment';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';

function Index() {
  const jApi = useApi();
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);
  const { user, getUser } = useContext(AuthContext);
  const [accounts, setAccounts] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState();

  const fetchData = async () => {
    try {
      setErrorMessage("");
      setIsLoading(true);
      const response = await jApi.request('GET', 'cma/account');

      const options = response.data
        .map(item => `${item.name} (${item.code})`)
        .sort((a, b) => a.localeCompare(b));

      setAccounts(options);

      let selected_account = sessionStorage.getItem('account');
      let selectedItem = options.find(option => option.includes("(" + selected_account + ")"));
      if (selectedItem) {
        setSelectedAccount(selectedItem);
      } else {
        setSelectedAccount(options[0]);
      }
    } catch (ex) {
      setErrorMessage(ex.toString());
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  function extractCodeFromLabel(label) {
    const match = label.match(/\((.*?)\)/);
    return match ? match[1] : null;
  }

  const handleOnChange = (event, newValue) => {
    if (!newValue) return;
    setSelectedAccount(newValue);
    sessionStorage.setItem('account', extractCodeFromLabel(newValue));
  };

  return (
    <>
      <Box
        sx={{
          minHeight: '100vh',
        }}
      >
        <Container component="main" maxWidth="xl" sx={{ pt: 3, pb: 6 }}>
          {/* Always show error if present */}
          {errorMessage && (
            <Fade in>
              <Paper
                sx={{
                  p: 2,
                  mb: 3,
                  background: alpha(theme.palette.error.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                  borderRadius: 2,
                }}
              >
                <Typography variant="body1" color="error.main">
                  {errorMessage}
                </Typography>
              </Paper>
            </Fade>
          )}

          {/* Hero Section */}
          <Fade in timeout={600}>
            <Paper
              sx={{
                p: 4,
                mb: 4,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
                backdropFilter: 'blur(20px)',
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                borderRadius: 3,
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, transparent 100%)`,
                  opacity: 0.5,
                },
              }}
            >
              <Grid container spacing={3} sx={{ position: 'relative', zIndex: 1 }}>
                {/* Greeting */}
                <Grid size={12}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: 3,
                        overflow: 'hidden',
                        boxShadow: `0 8px 16px ${alpha(theme.palette.common.black, 0.2)}`,
                        border: `2px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
                      }}
                    >
                      <img width="100%" height="100%" src={ImageHello} alt="Logo" style={{ objectFit: 'cover' }} />
                    </Box>
                    <Box>
                      <Typography
                        variant="h3"
                        sx={{
                          fontWeight: 700,
                          background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.primary.main} 100%)`,
                          backgroundClip: 'text',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                        }}
                      >
                        Hey {user?.display_name?.split(" ")[0] || 'there'}!
                      </Typography>
                      <Typography variant="h6" color="text.secondary">
                        What's shaking today?
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>

              </Grid>
            </Paper>
          </Fade>

        {/* Main tiles - Asymmetric Grid */}
        {!isLoading && (
          <Grid container spacing={3}>
            {/* Row 1: Three medium tiles */}
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Grow in timeout={1100}>
                <Box>
                  <TileReport account={selectedAccount ? extractCodeFromLabel(selectedAccount) : null} />
                </Box>
              </Grow>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Grow in timeout={1200}>
                <Box>
                  <TileAssessment />
                </Box>
              </Grow>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Grow in timeout={1300}>
                <Box>
                  <TileProcessingCenter />
                </Box>
              </Grow>
            </Grid>

            {/* Row 2: Three tiles */}
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Grow in timeout={1400}>
                <Box>
                  <TileAuditCenter />
                </Box>
              </Grow>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Grow in timeout={1500}>
                <Box>
                  <TileOperationsCenter />
                </Box>
              </Grow>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Grow in timeout={1600}>
                <Box>
                  <TileFinanceCenter account={selectedAccount ? extractCodeFromLabel(selectedAccount) : null} />
                </Box>
              </Grow>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Grow in timeout={1700}>
                <Box>
                  <TileQbr />
                </Box>
              </Grow>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Grow in timeout={1800}>
                <Box>
                                    <TileCustomerCenter />
                </Box>
              </Grow>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Grow in timeout={1900}>
                <Box>
                  <TileWarrantyCenter />
                </Box>
              </Grow>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Grow in timeout={2000}>
                <Box>
                  <TileFleetReport />
                </Box>
              </Grow>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Grow in timeout={2100}>
                <Box>
                  <TileEndpointDevices />
                </Box>
              </Grow>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Grow in timeout={2200}>
                <Box>
                  <TileBotReports />
                </Box>
              </Grow>
            </Grid>

          </Grid>
        )}

        {/* Optional: no accounts message */}
        {!isLoading && !selectedAccount && accounts?.length === 0 && (
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              No accounts found. Contact support or check your permissions.
            </Typography>
          </Box>
        )}
      </Container>
      </Box>
    </>
  );
}

export default Index;